import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useGuideStore } from '../../../store/guideStore';
import { Input } from '../../ui';
import { debounce } from 'lodash';
import { getGuideWidgetUrl } from '@/lib/utils';

const WidgetConfig: React.FC = () => {
  const { t } = useTranslation();
  const { currentGuide, updateGuideSettings } = useGuideStore();
  const [localGuide, setLocalGuide] = useState(currentGuide);
  const localGuideRef = useRef(localGuide);
  const debouncedSyncRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    localGuideRef.current = localGuide;
  }, [localGuide]);

  // Update local state when currentGuide changes
  React.useEffect(() => {
    if (currentGuide) {
      setLocalGuide(currentGuide);
    }
  }, [currentGuide]);

  // Create the sync function that always gets the latest localGuide
  const syncSettings = useCallback(async () => {
    const currentLocalGuide = localGuideRef.current;
    if (!currentLocalGuide) return;
    
    try {
      await updateGuideSettings(currentLocalGuide.id, {
        publicUrl: currentLocalGuide.publicUrl,
        isPublished: currentLocalGuide.isPublished,
        publishSettings: currentLocalGuide.publishSettings,
      });
    } catch (error) {
      console.error('Error updating widget settings:', error);
    }
  }, [updateGuideSettings]);

  // Create debounced version once
  useEffect(() => {
    debouncedSyncRef.current = debounce(syncSettings, 300);
    
    // Cleanup on unmount
    return () => {
      if (debouncedSyncRef.current) {
        debouncedSyncRef.current.cancel();
      }
    };
  }, [syncSettings]);

  const handlePublishSettingsChange = (updates: Partial<NonNullable<typeof localGuide>['publishSettings']>) => {
    if (!localGuide) return;
    
    const updatedGuide = {
      ...localGuide,
      publishSettings: { ...localGuide.publishSettings, ...updates }
    };

    setLocalGuide(updatedGuide);
    debouncedSyncRef.current?.();
  };

  const getWidgetDisplayProps = () => {
    if (!localGuide) return { mode: 'full', width: '100%', height: '100%' };
    
    const settings = localGuide.publishSettings;
    if (settings.widgetSizeMode === 'full') {
      return { mode: 'full', width: '100%', height: '100%' };
    } else {
      return {
        mode: 'fixed',
        width: `${settings.widgetWidth}px`,
        height: `${settings.widgetHeight}px`
      };
    }
  };

  const getWidgetEmbedCode = () => {
    if (!localGuide) return '';
    
    const settings = getWidgetDisplayProps();
    const url = getGuideWidgetUrl(localGuide);
    return `<iframe src="${url}" width="${settings.width}" height="${settings.height}" allowfullscreen="allowfullscreen" frameborder="0"></iframe>`;
  };

  const getWidgetPreviewUrl = () => {
    if (!localGuide) return '';
    return getGuideWidgetUrl(localGuide) + '&previewMode=1';
  };

  if (!localGuide) {
    return <div>{t('guide.widget.noGuideSelected')}</div>;
  }

  const widgetDisplayProps = getWidgetDisplayProps();

  return (
    <div className="bg-white border shadow-lg p-8 mb-6 container w-[750px] mx-auto">
      <div className="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
        <div className="md:col-span-5">
          <div className="space-y-2 flex flex-row items-center justify-between">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {t('guide.widget.sizeLabel')}
              </label>
              <p className="text-[0.8rem] text-muted-foreground">
                {t('guide.widget.sizeDescription')}
              </p>
            </div>
            <div className="flex flex-col items-center">
              <select
                value={localGuide.publishSettings.widgetSizeMode}
                onChange={(e) => handlePublishSettingsChange({ widgetSizeMode: e.target.value as 'full' | 'fixed' })}
                className="mt-0.5 py-1 w-fit rounded-lg border-gray-300 text-gray-700 text-xs"
              >
                <option value="full">
                  {t('guide.widget.sizeResponsive')}
                </option>
                <option value="fixed">{t('guide.widget.sizeFixed')}</option>
              </select>
              
              {localGuide.publishSettings.widgetSizeMode === 'fixed' && (
                <div className="flex mt-2 justify-center items-center">
                  <Input
                    type="number"
                    min={300}
                    max={1000}
                    value={localGuide.publishSettings.widgetWidth || ''}
                    onChange={(e) => handlePublishSettingsChange({ widgetWidth: parseInt(e.target.value) || 0 })}
                    className="h-6 text-gray-500 text-sm border rounded px-2 !w-[60px] mr-2"
                  />
                  <span className="text-xs">x</span>
                  <Input
                    type="number"
                    min={300}
                    max={1000}
                    value={localGuide.publishSettings.widgetHeight || ''}
                    onChange={(e) => handlePublishSettingsChange({ widgetHeight: parseInt(e.target.value) || 0 })}
                    className="h-6 text-gray-500 text-sm border rounded px-2 !w-[60px] ml-2"
                  />
                  <span className="ml-1 text-xs">px</span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2 flex flex-col mt-5">
            <div className="space-y-0.5">
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                {t('guide.widget.embedLabel')}
              </label>
              <p className="text-[0.8rem] text-muted-foreground">
                {t('guide.widget.embedDescription')}
              </p>
            </div>
            <textarea
              disabled
              className="mt-2 w-full rounded-lg border-gray-200 align-top shadow-sm text-sm bg-gray-100"
              value={getWidgetEmbedCode()}
              rows={3}
              onClick={(e) => (e.target as HTMLTextAreaElement).select()}
              onFocus={(e) => e.target.select()}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center flex-col mt-8">
        <span className="text-lg font-semibold mx-auto my-3">{t('guide.widget.preview')}</span>
        <iframe
          src={getWidgetPreviewUrl()}
          className="rounded shadow-xl border border-slate-200 mx-auto my-3"
          width={widgetDisplayProps.mode === 'full' ? '550px' : widgetDisplayProps.width}
          height={widgetDisplayProps.mode === 'full' ? '500px' : widgetDisplayProps.height}
          title={t('guide.widget.preview')}
        />
      </div>
    </div>
  );
};

export default WidgetConfig;
